import { useReducer, useCallback, useRef } from 'react';
import { ResizeState, ResizeHandlers, ResizeConfig, ResizeConstraints } from '../types/resize';
import { RESIZE_CONFIG, SCHEDULE_START_MINUTE, SCHEDULE_END_MINUTE } from '../config/constants';
import { calculateResizeNewTimes } from '../utils/resizeUtils';
import { resizeStateReducer, createResizeAction, validateResizeTransition } from '../utils/resizeStateTransitions';

/**
 * Custom hook for managing schedule card resize state and operations
 */
export const useScheduleResize = (
  config: Partial<ResizeConfig> = {},
  constraints: Partial<ResizeConstraints> = {}
) => {
  // Merge default config with provided config
  const resizeConfig: ResizeConfig = {
    ...RESIZE_CONFIG,
    ...config
  };

  // Merge default constraints with provided constraints
  const resizeConstraints: ResizeConstraints = {
    scheduleStartMinute: SCHEDULE_START_MINUTE,
    scheduleEndMinute: SCHEDULE_END_MINUTE,
    minDurationMinutes: resizeConfig.minDurationMinutes,
    snapInterval: resizeConfig.snapIntervalMinutes,
    ...constraints
  };

  // Initialize resize state with reducer
  const initialState: ResizeState = {
    isResizing: false,
    itemId: null,
    resizeEdge: null,
    originalStartTime: '',
    originalEndTime: '',
    currentStartTime: '',
    currentEndTime: '',
    startMouseY: 0
  };

  const [resizeState, dispatch] = useReducer(resizeStateReducer, initialState);

  // Ref to track if fine adjustment key is pressed
  const fineAdjustmentRef = useRef<boolean>(false);

  /**
   * Snap time to the appropriate interval based on fine adjustment mode
   */
  const snapToInterval = useCallback((minutes: number, interval: number): number => {
    return Math.round(minutes / interval) * interval;
  }, []);

  /**
   * Enforce time constraints (boundaries and minimum duration)
   */
  const enforceConstraints = useCallback((startMinutes: number, endMinutes: number) => {
    // Ensure start time is within schedule bounds
    const constrainedStart = Math.max(
      resizeConstraints.scheduleStartMinute,
      Math.min(startMinutes, resizeConstraints.scheduleEndMinute - resizeConstraints.minDurationMinutes)
    );

    // Ensure end time is within schedule bounds
    const constrainedEnd = Math.min(
      resizeConstraints.scheduleEndMinute,
      Math.max(endMinutes, constrainedStart + resizeConstraints.minDurationMinutes)
    );

    // Ensure minimum duration is maintained
    const duration = constrainedEnd - constrainedStart;
    if (duration < resizeConstraints.minDurationMinutes) {
      // If duration is too small, adjust based on which edge is being resized
      if (resizeState.resizeEdge === 'top') {
        // When resizing from top, maintain end time and adjust start
        return {
          startMinutes: constrainedEnd - resizeConstraints.minDurationMinutes,
          endMinutes: constrainedEnd
        };
      } else {
        // When resizing from bottom, maintain start time and adjust end
        return {
          startMinutes: constrainedStart,
          endMinutes: constrainedStart + resizeConstraints.minDurationMinutes
        };
      }
    }

    return {
      startMinutes: constrainedStart,
      endMinutes: constrainedEnd
    };
  }, [resizeConstraints, resizeState.resizeEdge]);

  /**
   * Calculate new time based on mouse position and resize edge
   * This function requires grid dimensions to be provided by the parent component
   */
  const calculateNewTime = useCallback((
    mouseY: number, 
    edge: 'top' | 'bottom',
    totalGridContentHeight?: number
  ) => {
    if (!resizeState.isResizing || !totalGridContentHeight) {
      return {
        startTime: resizeState.originalStartTime,
        endTime: resizeState.originalEndTime,
        isValid: false,
        violations: []
      };
    }

    // Calculate mouse movement delta
    const mouseDelta = mouseY - resizeState.startMouseY;
    
    // Use the utility function to calculate new times
    return calculateResizeNewTimes(
      resizeState.originalStartTime,
      resizeState.originalEndTime,
      mouseDelta,
      edge,
      totalGridContentHeight,
      resizeConfig.snapIntervalMinutes,
      resizeConfig.minDurationMinutes,
      resizeConstraints.scheduleStartMinute,
      resizeConstraints.scheduleEndMinute,
      fineAdjustmentRef.current,
      resizeConfig.fineAdjustmentInterval
    );
  }, [resizeState, resizeConfig, resizeConstraints]);

  /**
   * Start resize operation
   */
  const onResizeStart = useCallback((
    itemId: string, 
    edge: 'top' | 'bottom', 
    mouseY: number,
    originalStartTime?: string,
    originalEndTime?: string
  ) => {
    // Use provided times or placeholder values
    const startTime = originalStartTime || '09:00';
    const endTime = originalEndTime || '10:00';

    const action = createResizeAction.startResize(itemId, edge, mouseY, startTime, endTime);
    const validation = validateResizeTransition(resizeState, action);

    if (validation.isValid) {
      dispatch(action);
    } else {
      console.warn('Invalid resize transition:', validation.reason);
    }
  }, [resizeState]);

  /**
   * Handle resize movement
   * Requires totalGridContentHeight to be provided by the parent component
   */
  const onResizeMove = useCallback((mouseY: number, totalGridContentHeight?: number) => {
    if (!resizeState.isResizing || !resizeState.resizeEdge || !totalGridContentHeight) return;

    const newTimes = calculateNewTime(mouseY, resizeState.resizeEdge, totalGridContentHeight);
    
    // Always update the current times, even if there are violations
    // The visual feedback will show the constraints
    const action = createResizeAction.updateResize(newTimes.startTime, newTimes.endTime);
    const validation = validateResizeTransition(resizeState, action);

    if (validation.isValid) {
      dispatch(action);
    }
  }, [resizeState, calculateNewTime]);

  /**
   * End resize operation
   */
  const onResizeEnd = useCallback(() => {
    const action = createResizeAction.endResize();
    const validation = validateResizeTransition(resizeState, action);

    if (validation.isValid) {
      // The final times are in resizeState.currentStartTime and resizeState.currentEndTime
      // These will be applied to the actual schedule entry by the parent component
      dispatch(action);
    }
  }, [resizeState]);

  /**
   * Cancel resize operation
   */
  const onResizeCancel = useCallback(() => {
    const action = createResizeAction.cancelResize();
    const validation = validateResizeTransition(resizeState, action);

    if (validation.isValid) {
      dispatch(action);
    }
  }, [resizeState]);

  /**
   * Handle keyboard events for fine adjustment and cancellation
   */
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === resizeConfig.fineAdjustmentKey) {
      fineAdjustmentRef.current = true;
    } else if (event.key === 'Escape' && resizeState.isResizing) {
      onResizeCancel();
    }
  }, [resizeConfig.fineAdjustmentKey, resizeState.isResizing, onResizeCancel]);

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (event.key === resizeConfig.fineAdjustmentKey) {
      fineAdjustmentRef.current = false;
    }
  }, [resizeConfig.fineAdjustmentKey]);

  // Create handlers object
  const handlers: ResizeHandlers = {
    onResizeStart,
    onResizeMove,
    onResizeEnd,
    onResizeCancel
  };

  return {
    resizeState,
    handlers,
    config: resizeConfig,
    constraints: resizeConstraints,
    calculateNewTime,
    snapToInterval,
    enforceConstraints,
    handleKeyDown,
    handleKeyUp,
    fineAdjustmentActive: fineAdjustmentRef.current
  };
};